import { createReducer, on } from '@ngrx/store';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import {
	ILegalHold,
	ILegalHoldFilter,
	ILegalHoldIdentitySnapshot,
} from './legal-hold.interface';
import { LegalHoldActions } from './legal-hold.actions';
import { IResponseError } from '../../service/interface';

export const legalHoldFeatureKey = 'legalHold';

export interface IState extends EntityState<ILegalHold> {
	socketConnected: boolean;
	loading: boolean;
	error?: IResponseError;
	filter: ILegalHoldFilter;
	selectedHold?: ILegalHold;
	// Sensitive; never persisted beyond the open identity-snapshot modal.
	identitySnapshot?: ILegalHoldIdentitySnapshot;
}

export const legalHoldAdapter: EntityAdapter<ILegalHold> =
	createEntityAdapter<ILegalHold>({
		selectId: (hold: ILegalHold) => hold.id,
	});

export const initialState: IState = legalHoldAdapter.getInitialState({
	socketConnected: false,
	loading: false,
	filter: {
		pagination: {
			count: 0,
			limit: 10,
			offset: 1,
		},
	},
});

export const reducer = createReducer(
	initialState,
	// ── API ──

	on(
		LegalHoldActions.loadHolds,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.loadHoldsSuccess,
		(state, action): IState =>
			legalHoldAdapter.setAll(action.holds, {
				...state,
				filter: {
					...state.filter,
					pagination: {
						...state.filter.pagination,
						count: action.pagination.count,
					},
				},
				loading: false,
				error: undefined,
			}),
	),
	on(
		LegalHoldActions.loadHoldsError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.loadHold,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
			selectedHold: undefined,
		}),
	),
	on(
		LegalHoldActions.loadHoldSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedHold: action.hold,
		}),
	),
	on(
		LegalHoldActions.loadHoldError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.placeHold,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.placeHoldSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedHold: action.hold,
		}),
	),
	on(
		LegalHoldActions.placeHoldError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.liftHold,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.liftHoldSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedHold: action.hold,
		}),
	),
	on(
		LegalHoldActions.liftHoldError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.recertifyHold,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.recertifyHoldSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedHold: action.hold,
		}),
	),
	on(
		LegalHoldActions.recertifyHoldError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.recordAuthorityNotification,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.recordAuthorityNotificationSuccess,
		(state): IState => ({
			...state,
			loading: false,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.recordAuthorityNotificationError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.recordProductionOrder,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
		}),
	),
	on(
		LegalHoldActions.recordProductionOrderSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			selectedHold: action.hold,
		}),
	),
	on(
		LegalHoldActions.recordProductionOrderError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),
	on(
		LegalHoldActions.loadIdentitySnapshot,
		(state): IState => ({
			...state,
			loading: true,
			error: undefined,
			identitySnapshot: undefined,
		}),
	),
	on(
		LegalHoldActions.loadIdentitySnapshotSuccess,
		(state, action): IState => ({
			...state,
			loading: false,
			error: undefined,
			identitySnapshot: action.snapshot,
		}),
	),
	on(
		LegalHoldActions.loadIdentitySnapshotError,
		(state, action): IState => ({
			...state,
			loading: false,
			error: action.error,
		}),
	),

	// ── IO ──

	on(
		LegalHoldActions.socketConnect,
		LegalHoldActions.socketReconnected,
		(state): IState => ({
			...state,
			socketConnected: true,
		}),
	),
	on(
		LegalHoldActions.socketDisconnected,
		LegalHoldActions.socketClose,
		(state): IState => ({
			...state,
			socketConnected: false,
		}),
	),
	on(
		LegalHoldActions.holdPlacedViaSocket,
		LegalHoldActions.holdLiftedViaSocket,
		LegalHoldActions.holdRecertifiedViaSocket,
		LegalHoldActions.holdReviewDueViaSocket,
		LegalHoldActions.holdProductionOrderRecordedViaSocket,
		(state, action): IState =>
			legalHoldAdapter.updateOne(
				{ id: action.hold.id, changes: action.hold },
				{
					...state,
					selectedHold:
						state.selectedHold?.id === action.hold.id
							? { ...state.selectedHold, ...action.hold }
							: state.selectedHold,
				},
			),
	),

	// ── UI ──

	on(
		LegalHoldActions.setOffset,
		(state, action): IState => ({
			...state,
			loading: true,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: action.offset,
				},
			},
		}),
	),
	on(
		LegalHoldActions.setLimit,
		(state, action): IState => ({
			...state,
			loading: true,
			error: undefined,
			filter: {
				...state.filter,
				pagination: {
					...state.filter.pagination,
					offset: 1,
					limit: action.limit,
				},
			},
		}),
	),
	on(
		LegalHoldActions.setSortOrder,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: action.sortBy as ILegalHoldFilter['sortBy'],
				orderBy: action.orderBy,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		LegalHoldActions.clearSortOrder,
		(state): IState => ({
			...state,
			filter: {
				...state.filter,
				sortBy: undefined,
				orderBy: undefined,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		LegalHoldActions.setFilter,
		(state, action): IState => ({
			...state,
			filter: {
				...state.filter,
				...action.data,
				pagination: {
					...state.filter.pagination,
					offset: 1,
				},
			},
		}),
	),
	on(
		LegalHoldActions.clearHoldDetail,
		(state): IState => ({
			...state,
			selectedHold: undefined,
		}),
	),
	on(
		LegalHoldActions.clearIdentitySnapshot,
		(state): IState => ({
			...state,
			identitySnapshot: undefined,
		}),
	),
	on(
		LegalHoldActions.clearLegalHoldError,
		(state): IState => ({
			...state,
			error: undefined,
		}),
	),
);
